/**
 * Commentary button — triggers on-demand ML inference.
 * Shows different states: ready, loading (with pipeline progress), loaded, error.
 */

import type { ProgressStep, InferenceProgress, InferenceMeta } from '@state/chat';
import { SparklesIcon } from '@components/icons';
import { PipelineProgress } from '../pipeline-progress';

interface CommentaryButtonProps {
  state: 'not-requested' | 'loading' | 'loaded' | 'error';
  onClick: () => void;
  progressSteps?: ProgressStep[];
  inferenceProgress?: InferenceProgress;
  inferenceMeta?: InferenceMeta;
}

export function CommentaryButton({
  state,
  onClick,
  progressSteps,
  inferenceProgress,
  inferenceMeta,
}: CommentaryButtonProps) {
  if (state === 'loaded') return null;

  if (state === 'loading') {
    if (progressSteps && progressSteps.length > 0) {
      return (
        <div className="commentary-progress">
          <PipelineProgress
            steps={progressSteps}
            inferenceProgress={inferenceProgress}
            inferenceMeta={inferenceMeta}
          />
        </div>
      );
    }
    return (
      <div className="commentary-progress commentary-progress-waiting">
        Connecting to AI server...
      </div>
    );
  }

  if (state === 'error') {
    return (
      <button className="commentary-button commentary-button-error" onClick={onClick}>
        Commentary unavailable — tap to retry
      </button>
    );
  }

  // One action, stated plainly — the card's call to action. The provenance
  // line ("AI-generated from Halley's, McGee, and Chuck Smith") lived here
  // until it out-weighed the action itself (UAT 2026-09-21).
  return (
    <button className="commentary-button" onClick={onClick}>
      <SparklesIcon className="commentary-button-icon" />
      Generate Commentary
    </button>
  );
}
